// Browser -> private Supabase Storage. Avoid proxying original media through Render.
// Uploads are resumable within Supabase's 24-hour TUS transfer window.
const CHUNK=6*1024*1024; // Supabase TUS requires this size.
const pause=ms=>new Promise(resolve=>setTimeout(resolve,ms));
// Supabase legacy media remains capped at 50 MB; new Takefold media travels
// directly to R2, whose upload service separately enforces server quotas.
export const LEGACY_MAX_MEDIA_BYTES=50_000_000;
const configuredMB=Number(import.meta.env?.VITE_R2_MEDIA_MB); // Independent of the old Supabase 50 MB deployment setting.
export const MAX_MEDIA_MB=Number.isFinite(configuredMB)&&configuredMB>=1&&configuredMB<=5_000
 ?configuredMB:5_000;
export const MAX_MEDIA_BYTES=Math.round(MAX_MEDIA_MB*1_000_000);
export const mediaLimitLabel=bytes=>{
 const mb=bytes/1_000_000;
 return mb>=1000?`${(mb/1000).toLocaleString(undefined,{maximumFractionDigits:1})} GB`:
  `${mb.toLocaleString(undefined,{maximumFractionDigits:1})} MB`;
};
export const MAX_MEDIA_DURATION=4*60*60; // Per-interview duration.
const storageKey=(owner,id)=>'takefold-upload-'+owner+'-'+id;
const safeLocal=(key,value)=>{try{if(value)localStorage.setItem(key,JSON.stringify(value));else localStorage.removeItem(key);}catch{/* In-session transfers still work. */}};
const encode=v=>btoa(String.fromCharCode(...new TextEncoder().encode(v)));
function endpointFor(client){
 const url=new URL(client.supabaseUrl);
 if(url.hostname.endsWith('.supabase.co'))url.hostname=url.hostname.replace(/\.supabase\.co$/,'.storage.supabase.co');
 return url.origin+'/storage/v1/upload/resumable';
}
async function ok(response,action){
 if(response.ok)return response;
 const hint=response.status===401?'Sign in again.':response.status===413?'This file exceeds your current storage limit.':'Re-select the same file to retry.';
 throw new Error(action+' failed (HTTP '+response.status+'). '+hint);
}
export async function uploadResumable(client,owner,id,path,file,onProgress){
 const endpoint=endpointFor(client),key=storageKey(owner,id);
 const result=await client.auth.getSession();
 const token=result.data?.session?.access_token;
 if(!token)throw new Error('Sign in before uploading.');
 const headers={authorization:'Bearer '+token,apikey:client.supabaseKey,'Tus-Resumable':'1.0.0'};
 let saved=null;
 try{saved=JSON.parse(localStorage.getItem(key)||'null')}catch{/* no saved upload */}
 let url=saved?.size===file.size&&saved?.modified===file.lastModified&&saved?.url||null;
 if(url&&new URL(url).origin!==new URL(endpoint).origin)url=null;
 let offset=0;
 if(url){
  let response;
  try{response=await fetch(url,{method:'HEAD',headers})}catch{throw new Error('Upload connection interrupted. Choose this file again to resume.')}
  if(response.status===404||response.status===410){url=null;safeLocal(key,null)}
  else{
   await ok(response,'Resume');
   offset=Number(response.headers.get('Upload-Offset'));
   if(!Number.isSafeInteger(offset)||offset<0||offset>file.size)throw new Error('Invalid resume offset returned by storage.');
  }
 }
 if(!url){
  const meta=[['bucketName','cutscript-media'],['objectName',path],['contentType',file.type||'video/mp4'],['cacheControl','3600']].map(pair=>pair[0]+' '+encode(pair[1])).join(',');
  const response=await ok(await fetch(endpoint,{method:'POST',headers:{...headers,'Upload-Length':String(file.size),'Upload-Metadata':meta}}),'Begin upload');
  const location=response.headers.get('Location');
  if(!location)throw new Error('Storage returned no resumable upload URL.');
  url=new URL(location,endpoint).href;
  if(new URL(url).origin!==new URL(endpoint).origin)throw new Error('Unexpected storage upload address.');
  safeLocal(key,{url,size:file.size,modified:file.lastModified});
 }
 const progress=bytes=>onProgress?.(Math.round(Math.min(100,bytes/file.size*100)));
 progress(offset);
 while(offset<file.size){
  const end=Math.min(file.size,offset+CHUNK);
  let sent=false;
  for(let attempt=0;attempt<4;attempt++){
   if(attempt)await pause(attempt*1000);
   try{
    const response=await fetch(url,{method:'PATCH',headers:{...headers,'Content-Type':'application/offset+octet-stream','Upload-Offset':String(offset)},body:file.slice(offset,end)});
    if(!response.ok&&response.status!==409)await ok(response,'Upload');
    if(response.status===409){
     const check=await ok(await fetch(url,{method:'HEAD',headers}),'Resume');
     const current=Number(check.headers.get('Upload-Offset'));
     if(Number.isSafeInteger(current)&&current>offset&&current<=file.size){offset=current;sent=true;break;}
     throw new Error('Concurrent upload conflict.');
    }
    const reported=Number(response.headers.get('Upload-Offset'));
    offset=Number.isSafeInteger(reported)&&reported>=end?reported:end;
    sent=true;break;
   }catch(e){
    if(attempt===3)throw new Error('Upload paused at '+Math.round(offset/file.size*100)+'%. Re-select the same video to resume. '+e.message);
    try{
     const check=await fetch(url,{method:'HEAD',headers});
     if(check.ok){
      const current=Number(check.headers.get('Upload-Offset'));
      if(Number.isSafeInteger(current)&&current>offset&&current<=file.size){offset=current;sent=true;break;}
     }
    }catch{/* Retrying the same upload session. */}
   }
  }
  if(!sent)throw new Error('Upload could not finish. Re-select the file to resume.');
  progress(offset);
 }
 safeLocal(key,null);
 progress(file.size);
}
